(function pfgSw1BuilderV3CoreFactory(root, factory) {
  'use strict';
  const catalog = root?.PFGSw1BuilderV3Catalog || (typeof require === 'function' ? require('./pfg-sw1-builder-v3-catalog.js') : null);
  const manifest = root?.PFGSw1BuilderV3Manifest || (typeof require === 'function' ? require('./pfg-sw1-builder-v3-manifest.js') : null);
  const core = factory(catalog, manifest);
  if (typeof module === 'object' && module.exports) module.exports = core;
  if (root) root.PFGSw1BuilderV3Core = core;
})(typeof window !== 'undefined' ? window : globalThis, function createPfgSw1BuilderV3Core(catalog, manifest) {
  'use strict';
  if (!catalog || !manifest) throw new Error('PFG SW1 Builder V3 core requires the catalog and manifest.');

  const CORE_VERSION = '3.37.0';
  const FACE_BUTTON_SCALE = 1;
  const FACE_BUTTON_PARTS = new Set(['X', 'Y', 'A', 'B', 'up', 'left', 'right', 'down']);
  const URL_KEYS = Object.freeze({
    layout: 'layout',
    family: 'family',
    pairMode: 'pair',
    shell: 'shell',
    rightShell: 'right_shell',
    buttonPairMode: 'button_pair',
    buttonColor: 'buttons',
    rightButtonColor: 'right_buttons',
    accent: 'accent',
    faceStyle: 'face',
    capColor: 'caps',
    angle: 'angle',
    design: 'design'
  });
  const LEGACY_FACE_KEYS = Object.freeze({ blank: 'aftermarket', lettered: 'official-lettered' });
  const LEGACY_BUTTON_KEYS = Object.freeze({ white: 'opaque-white' });
  const cloneState = (state) => ({ ...catalog.DEFAULT_STATE, ...(state || {}) });
  const hasKey = (items, key) => items.some((item) => item.key === key);

  function availableShells(state) {
    return catalog.SHELLS.filter((shell) => shell.family === state.family && shell.layouts.includes(state.layout));
  }

  function availableRightShells(state) {
    // A D-pad changes only the left Joy-Con. In independent mode the right
    // Joy-Con keeps its normal ABXY face, so every registered shell finish is
    // compatible even when that finish has no dedicated D-pad photograph.
    if (state.layout === 'd-pad' && state.pairMode === 'independent') return catalog.SHELLS;
    return availableShells(state);
  }

  function availableButtonSets(state) {
    return catalog.BUTTON_SETS.filter((buttons) => buttons.layouts.includes(state.layout) && buttons.families.includes(state.family));
  }

  function availableRightButtonSets(state) {
    if (state.buttonPairMode !== 'independent') return availableButtonSets(state);
    const family = catalog.getShell(state.rightShell)?.family || state.family;
    return catalog.BUTTON_SETS.filter((buttons) => buttons.families.includes(family));
  }

  function availableDesigns(state) {
    return catalog.DESIGNS.filter((design) => design.families.includes(state.family) && design.layouts.includes(state.layout));
  }

  function normalizeState(input) {
    const state = cloneState(input);
    state.buttonColor = LEGACY_BUTTON_KEYS[state.buttonColor] || state.buttonColor;
    state.faceStyle = LEGACY_FACE_KEYS[state.faceStyle] || state.faceStyle;
    if (!hasKey(catalog.LAYOUTS, state.layout)) state.layout = catalog.DEFAULT_STATE.layout;
    if (!hasKey(catalog.FAMILIES, state.family)) state.family = catalog.DEFAULT_STATE.family;

    if (!['matching', 'independent'].includes(state.pairMode)) state.pairMode = catalog.DEFAULT_STATE.pairMode;

    const requestedShell = catalog.SHELLS.find((shell) => shell.key === state.shell);
    if (requestedShell && requestedShell.layouts.includes(state.layout)) state.family = requestedShell.family;
    else state.shell = availableShells(state)[0]?.key || catalog.DEFAULT_STATE.shell;
    const shells = availableShells(state);
    if (!shells.some((shell) => shell.key === state.shell)) state.shell = shells[0]?.key || catalog.DEFAULT_STATE.shell;
    if (state.pairMode === 'matching') state.rightShell = state.shell;
    else if (!availableRightShells(state).some((shell) => shell.key === state.rightShell)) state.rightShell = state.shell;

    // A retired face-style link becomes a visible button option. It cannot
    // silently override a later face preset, including on D-pad builds.
    if (state.faceStyle === 'official-lettered') {
      state.rightButtonColor = 'black-oem';
      state.buttonPairMode = state.buttonColor === 'black-oem' ? 'matching' : 'independent';
      state.accent = 'matching';
    }
    state.faceStyle = 'aftermarket';
    const buttonSets = availableButtonSets(state);
    if (!buttonSets.some((buttons) => buttons.key === state.buttonColor)) state.buttonColor = buttonSets[0]?.key || catalog.DEFAULT_STATE.buttonColor;
    if (!['matching', 'independent'].includes(state.buttonPairMode)) state.buttonPairMode = catalog.DEFAULT_STATE.buttonPairMode;
    state.rightButtonColor = LEGACY_BUTTON_KEYS[state.rightButtonColor] || state.rightButtonColor;
    if (state.buttonPairMode === 'matching') state.rightButtonColor = state.buttonColor;
    else if (!availableRightButtonSets(state).some((buttons) => buttons.key === state.rightButtonColor)) state.rightButtonColor = state.buttonColor;
    if (!hasKey(catalog.ANGLES, state.angle)) state.angle = catalog.DEFAULT_STATE.angle;
    if (!catalog.getShell(state.shell)?.views.includes(state.angle) || !catalog.getShell(state.rightShell)?.views.includes(state.angle)) state.angle = 'front';
    if (!hasKey(catalog.ACCENTS, state.accent)) state.accent = catalog.DEFAULT_STATE.accent;
    if (!hasKey(catalog.FACE_STYLES, state.faceStyle)) state.faceStyle = catalog.DEFAULT_STATE.faceStyle;
    if (!hasKey(catalog.CAPS, state.capColor)) state.capColor = catalog.DEFAULT_STATE.capColor;

    const designs = availableDesigns(state);
    if (!designs.some((design) => design.key === state.design)) state.design = 'none';
    return state;
  }

  function switchFamily(input, requestedFamily) {
    const state = normalizeState(input);
    if (!hasKey(catalog.FAMILIES, requestedFamily)) return state;
    state.family = requestedFamily;
    state.shell = catalog.SHELLS.find((shell) => shell.family === requestedFamily && shell.layouts.includes(state.layout))?.key || state.shell;
    state.rightShell = state.shell;
    const requestedDesign = catalog.getDesign(state.design);
    if (!requestedDesign?.families.includes(requestedFamily) || !requestedDesign?.layouts.includes(state.layout)) state.design = 'none';
    return normalizeState(state);
  }

  function switchLayout(input, requestedLayout) {
    const state = normalizeState(input);
    if (!hasKey(catalog.LAYOUTS, requestedLayout)) return state;
    state.layout = requestedLayout;
    const shell = catalog.getShell(state.shell);
    if (!shell.layouts.includes(requestedLayout)) state.shell = catalog.SHELLS.find((candidate) => candidate.family === state.family && candidate.layouts.includes(requestedLayout))?.key || catalog.DEFAULT_STATE.shell;
    const rightShell = catalog.getShell(state.rightShell);
    const rightSupportsRequestedLayout = requestedLayout === 'd-pad' && state.pairMode === 'independent'
      ? Boolean(rightShell)
      : rightShell?.layouts.includes(requestedLayout);
    if (!rightSupportsRequestedLayout) state.rightShell = state.pairMode === 'matching'
      ? state.shell
      : catalog.SHELLS.find((candidate) => candidate.family === state.family && candidate.layouts.includes(requestedLayout))?.key || state.shell;
    return normalizeState(state);
  }

  function layer(id) {
    const asset = manifest.assets[id];
    if (!asset) throw new Error(`Manifest references unknown asset: ${id}`);
    return asset;
  }

  function clippedShellLayer(id, side) {
    const asset = layer(id);
    return Object.freeze({ ...asset, id: `${asset.id}@${side}`, baseId: asset.id, clipSide: side });
  }

  function clippedControlLayer(id, side) {
    const asset = layer(id);
    return Object.freeze({ ...asset, id: `${asset.id}@${side}`, baseId: asset.id, clipSide: side });
  }

  function resolveButtonPlan(input) {
    const state = normalizeState(input);
    const selected = catalog.getAccent(state.accent);
    const kit = selected?.supplierSku ? selected : null;
    const faceParts = { left: state.layout === 'd-pad' ? ['dpad'] : ['up', 'left', 'right', 'down'], right: ['X', 'Y', 'A', 'B'] };
    const sides = {};
    ['left', 'right'].forEach(side => {
      const installed = [...faceParts[side], ...(side === 'left' ? ['minus', 'capture'] : ['plus', 'home'])];
      sides[side] = Object.freeze({
        buttonSet: catalog.getButtonSet(side === 'left' ? state.buttonColor : state.rightButtonColor),
        faceParts: Object.freeze(faceParts[side]),
        kitParts: Object.freeze(installed.filter(part => kit?.includedParts.includes(part)))
      });
    });
    return Object.freeze({ state: Object.freeze(state), kit, sides: Object.freeze(sides) });
  }

  function resolveGeometry(input) {
    const state = normalizeState(input);
    const profiles = manifest.compositions.catalogGeometry?.profiles || {};
    const sides = {};
    ['left', 'right'].forEach((side) => {
      const key = state.design !== 'none' ? state.design : side === 'left' ? state.shell : state.rightShell;
      const layout = state.design !== 'none' || side === 'left' ? state.layout : 'four-arrow';
      sides[side] = profiles[`${key}|${layout}|${state.angle}`]?.[side]
        || (state.angle === 'back' ? profiles[`${key}|four-arrow|back`]?.[side] : null) || null;
    });
    return Object.freeze(sides);
  }

  function resolveArtworkMask(input, side) {
    const state = normalizeState({ ...input, angle: 'front' });
    const profile = resolveGeometry(state)[side];
    if (profile?.artworkMaskId) return layer(profile.artworkMaskId);
    const shell = catalog.getShell(side === 'left' ? state.shell : state.rightShell);
    return layer(manifest.compositions.artworkMasks[shell.family][state.layout]);
  }

  function registerControlLayer(asset, profile = null) {
    const geometry = manifest.compositions.catalogGeometry;
    const sources = geometry.componentSets[geometry.componentFiles[asset.file]];
    if (!sources) throw new Error(`Missing component geometry for ${asset.file}.`);
    const family = asset.file.match(/-(solid|transparent)-/)?.[1];
    const view = profile?.view || (asset.slot === 'rear-controls' ? 'back' : 'front');
    const coverage = profile
      ? manifest.compositions.bumperCoverage.profiles[profile.id]?.[family]
      : manifest.compositions.bumperCoverage.generic[`${family}|${view}`];
    const fragments = Object.entries(sources).filter(([, source]) =>
      (source.clip[0] + source.clip[2] < 1600 ? 'left' : 'right') === asset.clipSide
    ).map(([part, source]) => {
      const originalTarget = profile ? profile.controls[part] : source.clip;
      if (!originalTarget) throw new Error(`Missing ${profile.id} ${part} registration.`);
      const [left,top,right,bottom] = originalTarget;
      const scale = FACE_BUTTON_PARTS.has(part) ? FACE_BUTTON_SCALE : 1;
      const dx = (right-left)*(scale-1)/2, dy = (bottom-top)*(scale-1)/2;
      const target = [left-dx,top-dy,right+dx,bottom+dy];
      const [x0, y0, x1, y1] = source.clip;
      const sx = (target[2] - target[0]) / (x1 - x0);
      const sy = (target[3] - target[1]) / (y1 - y0);
      const tx = target[0] - x0 * sx, ty = target[1] - y0 * sy;
      const [a,b,c,d,e,f] = profile?.stageTransform || [1,0,0,1,0,0];
      return Object.freeze({ part, clip: source.clip, clipPath: source.path,
        ...(coverage?.[part] || {}),
        transform: [a*sx,b*sx,c*sy,d*sy,a*tx+c*ty+e,b*tx+d*ty+f] });
    });
    return Object.freeze({ ...asset, ...(profile ? {geometryProfile: profile.id} : {}), fragments: Object.freeze(fragments) });
  }

  function supplierControlLayer(kit, side, slot, parts, family, profile) {
    const source = manifest.compositions.supplierFaceKits[kit.key];
    if (!source) throw new Error(`Missing supplier photographs for ${kit.key}.`);
    const asset = { ...clippedControlLayer(source.assetIds[slot], side),
      id: `${source.assetIds[slot]}.${slot}@${side}`, slot };
    const geometry = manifest.compositions.catalogGeometry;
    const group = slot === 'controls-abxy' ? 'abxy' : slot === 'controls-direction' ? 'four-arrow' : 'common';
    const referenceFile = `pfg-sw1-builder-v3-controls-opaque-white-${family}-front-${group}.webp`;
    const references = geometry.componentSets[geometry.componentFiles[referenceFile]];
    const fragments = parts.map(part => {
      const component = source.components[part];
      const target = profile?.controls[part] || references?.[part]?.clip;
      if (!component || !target) throw new Error(`Missing ${kit.key} ${part} supplier registration.`);
      const [l,t,r,b] = target, scale = FACE_BUTTON_PARTS.has(part) ? FACE_BUTTON_SCALE : 1;
      const [x0,y0,x1,y1] = component.clip;
      const width = (r-l)*scale, height = (b-t)*scale;
      const sx = width/(x1-x0), sy = height/(y1-y0);
      const tx = (l+r-width)/2-x0*sx, ty = (t+b-height)/2-y0*sy;
      const [a,b0,c,d,e,f] = profile?.stageTransform || [1,0,0,1,0,0];
      return Object.freeze({ part, clip: component.clip, clipPath: component.path,
        transform: [a*sx,b0*sx,c*sy,d*sy,a*tx+c*ty+e,b0*tx+d*ty+f] });
    });
    return Object.freeze({ ...asset, supplierFaceKit: kit.key, supplierSku: kit.supplierSku,
      sourceProductUrl: kit.productUrl, sourceSha256: source.sourceSha256,
      sourceSize: source.sourceSize, ...(profile ? { geometryProfile: profile.id } : {}), fragments: Object.freeze(fragments) });
  }

  function resolveLayers(input) {
    const state = normalizeState(input);
    const composition = manifest.compositions;
    const geometry = resolveGeometry(state);
    const buttonPlan = resolveButtonPlan(state);
    const leftShell = catalog.getShell(state.shell);
    const rightShell = catalog.getShell(state.rightShell);
    const leftFamily = leftShell.family;
    const rightFamily = rightShell.family;
    const leftShellComposition = composition.shells[state.shell];
    const leftUsesPhotographicDpad = state.angle === 'front' && state.layout === 'd-pad' && state.shell !== 'white' && Boolean(leftShellComposition?.frontDpad);
    const leftShellId = leftUsesPhotographicDpad ? leftShellComposition.frontDpad : leftShellComposition?.[state.angle];
    const rightShellId = composition.shells[state.rightShell]?.[state.angle];
    const leftDpadOccluderId = composition.shellDpadOccluders?.[state.shell];
    const leftShadowId = composition.shadows[leftFamily]?.[state.angle];
    const rightShadowId = composition.shadows[rightFamily]?.[state.angle];
    const leftControls = composition.controls[state.buttonColor]?.[leftFamily];
    const rightControls = composition.controls[state.rightButtonColor]?.[rightFamily];
    const layers = [
      clippedShellLayer(leftShellId, 'left'),
      clippedShellLayer(rightShellId, 'right')
    ];
    if (state.angle === 'front' && state.layout === 'd-pad' && !leftUsesPhotographicDpad) layers.push(clippedShellLayer(leftDpadOccluderId, 'left'));

    const selectedDesign = state.design !== 'none' ? composition.designs[state.design] : null;
    const designLayoutToken = state.layout === 'd-pad' ? 'Dpad' : 'FourArrow';

    // A premade is a complete registered photographic shell replacement.
    // Place it directly above the ordinary shell, then restore the common
    // studio shadow and selectable controls above it.  Treating the photo as
    // a partial decal exposed the selected shell around its inner edge and
    // changed the apparent controller size.
    if (selectedDesign) layers.push(layer(selectedDesign[`${state.angle}${designLayoutToken}`]));
    layers.push(
      clippedControlLayer(leftShadowId, 'left'),
      clippedControlLayer(rightShadowId, 'right')
    );

    if (state.angle === 'back') {
      layers.push(
        clippedControlLayer(leftControls?.back, 'left'),
        clippedControlLayer(rightControls?.back, 'right')
      );
    } else {
      layers.push(
        clippedControlLayer(leftControls?.front?.common, 'left'),
        clippedControlLayer(rightControls?.front?.common, 'right')
      );

      // Partial kits use the supplier's actual photographed components.
      // Jelly includes system buttons; shoulders and rails stay with the full kit.
      if (buttonPlan.kit) ['left', 'right'].forEach(side => {
        const parts = buttonPlan.sides[side].kitParts.filter(part => !FACE_BUTTON_PARTS.has(part));
        if (parts.length) layers.push(supplierControlLayer(buttonPlan.kit, side, 'controls-common', parts,
          side === 'left' ? leftFamily : rightFamily, geometry[side]));
      });
      if (buttonPlan.kit) layers.push(supplierControlLayer(buttonPlan.kit, 'right', 'controls-abxy', buttonPlan.sides.right.faceParts, rightFamily, geometry.right));
      else layers.push(clippedControlLayer(rightControls?.front?.abxy, 'right'));

      // Partial kits contain no D-pad; the chosen full kit still supplies it.
      if (state.layout === 'd-pad') layers.push(clippedControlLayer(leftControls?.front?.dpad, 'left'));
      else if (buttonPlan.kit) layers.push(supplierControlLayer(buttonPlan.kit, 'left', 'controls-direction', buttonPlan.sides.left.faceParts, leftFamily, geometry.left));
      else layers.push(clippedControlLayer(leftControls?.front?.fourArrow, 'left'));
      layers.push(
        clippedControlLayer(composition.caps[state.capColor]?.[leftFamily], 'left'),
        clippedControlLayer(composition.caps[state.capColor]?.[rightFamily], 'right')
      );
    }

    const registered = layers.flatMap((asset) => {
      if (asset.supplierFaceKit) return [asset];
      const side = asset.clipSide;
      const profile = side && geometry[side];
      if (asset.slot === 'design' && selectedDesign && geometry.left && geometry.right) return [];
      if (!profile) {
        const roundFaces = asset.slot === 'controls-abxy' || (asset.slot === 'controls-direction' && state.layout === 'four-arrow');
        const shoulders = asset.slot === 'controls-common' || asset.slot === 'rear-controls';
        if (!roundFaces && !shoulders) return [asset];
        const registeredAsset = registerControlLayer(asset);
        return [asset.slot === 'controls-common' && buttonPlan.kit
          ? { ...registeredAsset, fragments: registeredAsset.fragments.filter(fragment => !buttonPlan.sides[side].kitParts.includes(fragment.part)) }
          : registeredAsset];
      }
      if (asset.slot === 'shell') return [{ ...clippedShellLayer(profile.assetId, side),
        geometryProfile: profile.id, photoTransform: profile.stageTransform }];
      if (asset.slot === 'shell-occluder' || asset.slot === 'shadow') return [];
      const registeredAsset = registerControlLayer(asset, profile);
      return [asset.slot === 'controls-common' && buttonPlan.kit
        ? { ...registeredAsset, fragments: registeredAsset.fragments.filter(fragment => !buttonPlan.sides[side].kitParts.includes(fragment.part)) }
        : registeredAsset];
    });
    const slots = registered.map((asset) => manifest.layerOrder.indexOf(asset.slot));
    if (slots.some((slot) => slot < 0) || slots.some((slot, index) => index > 0 && slot < slots[index - 1])) throw new Error('Resolved layer order violates the production manifest.');
    return Object.freeze({ state: Object.freeze({ ...state }), layers: Object.freeze(registered) });
  }

  function parsePreviewState(search, baseState = catalog.DEFAULT_STATE) {
    const params = search instanceof URLSearchParams ? search : new URLSearchParams(String(search || '').replace(/^\?/, ''));
    const next = { ...baseState };
    Object.entries(URL_KEYS).forEach(([stateKey, urlKey]) => {
      const value = params.get(urlKey);
      if (!value) return;
      if (stateKey === 'faceStyle') next[stateKey] = LEGACY_FACE_KEYS[value] || value;
      else if (stateKey === 'buttonColor' || stateKey === 'rightButtonColor') next[stateKey] = LEGACY_BUTTON_KEYS[value] || value;
      else next[stateKey] = value;
    });
    // Old shared-shell links stay valid: without an explicit right-side key,
    // both Joy-Cons inherit the legacy `shell` value.
    if (params.has(URL_KEYS.shell) && !params.has(URL_KEYS.rightShell)) next.rightShell = next.shell;
    if (!params.has(URL_KEYS.pairMode) && (params.has(URL_KEYS.shell) || params.has(URL_KEYS.rightShell))) next.pairMode = next.rightShell === next.shell ? 'matching' : 'independent';
    if (params.has(URL_KEYS.buttonColor) && !params.has(URL_KEYS.rightButtonColor)) next.rightButtonColor = next.buttonColor;
    if (!params.has(URL_KEYS.buttonPairMode) && (params.has(URL_KEYS.buttonColor) || params.has(URL_KEYS.rightButtonColor))) next.buttonPairMode = next.rightButtonColor === next.buttonColor ? 'matching' : 'independent';
    // Resolve malformed layout values before deriving a family-only shell.
    // Otherwise the lookup fails and the default solid shell can override an
    // explicitly requested transparent family during normalizeState().
    if (!hasKey(catalog.LAYOUTS, next.layout)) next.layout = catalog.DEFAULT_STATE.layout;
    if (!hasKey(catalog.FAMILIES, next.family)) next.family = catalog.DEFAULT_STATE.family;
    if (params.has(URL_KEYS.family) && !params.get(URL_KEYS.shell) && hasKey(catalog.FAMILIES, next.family)) next.shell = catalog.SHELLS.find((shell) => shell.family === next.family && shell.layouts.includes(next.layout))?.key || next.shell;
    return normalizeState(next);
  }

  function stateToSearchParams(input, source) {
    const state = normalizeState(input);
    const params = source instanceof URLSearchParams ? new URLSearchParams(source) : new URLSearchParams(String(source || '').replace(/^\?/, ''));
    Object.entries(URL_KEYS).forEach(([stateKey, urlKey]) => params.set(urlKey, state[stateKey]));
    return params;
  }

  function describeState(input) {
    const state = normalizeState(input);
    const layout = catalog.getLayout(state.layout)?.name;
    const leftShell = catalog.getShell(state.shell)?.name;
    const rightShell = catalog.getShell(state.rightShell)?.name;
    const shell = state.pairMode === 'matching' || state.shell === state.rightShell
      ? `${leftShell} matching pair`
      : `${leftShell} left / ${rightShell} right`;
    const leftButtons = catalog.getButtonSet(state.buttonColor)?.name;
    const rightButtons = catalog.getButtonSet(state.rightButtonColor)?.name;
    const buttons = state.buttonPairMode === 'matching' || state.buttonColor === state.rightButtonColor
      ? `${leftButtons} matching buttons`
      : `${leftButtons} left / ${rightButtons} right buttons`;
    if (state.angle === 'back') {
      const design = state.design !== 'none' ? ` · ${catalog.getDesign(state.design)?.name}` : '';
      return `${shell} rear view · ${buttons}${design}. Front-only control choices are preserved.`;
    }
    const caps = catalog.getCap(state.capColor)?.name;
    const design = catalog.getDesign(state.design)?.name;
    let accent = 'all matching controls';
    const selectedKit = catalog.getAccent(state.accent);
    if (selectedKit?.supplierSku) accent = `${selectedKit.name} face button kit${state.layout === 'd-pad' ? ' with selected D-pad' : ''}${selectedKit.kind === 'jelly' ? ', including +/−, Home and Capture' : ''}`;
    return `${shell} ${layout} shell · ${buttons} · ${accent} · ${caps} joystick caps · ${design}.`;
  }

  function accessibilityLabel(input) {
    const state = normalizeState(input);
    const leftShell = catalog.getShell(state.shell)?.name;
    const rightShell = catalog.getShell(state.rightShell)?.name;
    const shell = state.shell === state.rightShell ? leftShell : `${leftShell} left and ${rightShell} right`;
    const layout = state.layout === 'd-pad' ? 'D-pad' : 'four-arrow';
    const view = state.angle === 'back' ? 'rear' : 'front';
    const design = state.design !== 'none' ? ` with ${catalog.getDesign(state.design)?.name} print` : '';
    return `${shell} ${layout} Switch 1 Joy-Con ${view} preview${design}`;
  }

  function reviewWarnings(input) {
    normalizeState(input);
    return Object.freeze([]);
  }

  function createSequenceGate() {
    let current = 0;
    return Object.freeze({ next: () => { current += 1; return current; }, isCurrent: (token) => token === current, invalidate: () => { current += 1; return current; }, current: () => current });
  }

  function validateDataContract() {
    const errors = [];
    if (catalog.version !== CORE_VERSION) errors.push(`Catalog ${catalog.version} and core ${CORE_VERSION} versions differ.`);
    if (manifest.version !== CORE_VERSION) errors.push(`Manifest ${manifest.version} and core ${CORE_VERSION} versions differ.`);
    if (catalog.version !== manifest.version) errors.push(`Catalog ${catalog.version} and manifest ${manifest.version} versions differ.`);
    if (catalog.coordinateSpace.id !== manifest.coordinateSpace.id) errors.push('Catalog and manifest coordinate spaces differ.');
    if (catalog.coordinateSpace.width !== manifest.coordinateSpace.width || catalog.coordinateSpace.height !== manifest.coordinateSpace.height) errors.push('Catalog and manifest dimensions differ.');
    if (manifest.architecture !== 'pre-rendered-registered-dom-image-stack') errors.push('Unexpected rendering architecture.');
    if (manifest.previewOnly !== false) errors.push('The live builder manifest must enable checkout integration.');
    const filenames = new Set();
    Object.values(manifest.assets).forEach((asset) => {
      if (!asset.file || !asset.file.startsWith('pfg-sw1-builder-v3-')) errors.push(`Unnamespaced asset filename: ${asset.id}`);
      if (filenames.has(asset.file)) errors.push(`Duplicate asset filename: ${asset.file}`);
      filenames.add(asset.file);
      if (!manifest.layerOrder.includes(asset.slot)) errors.push(`Unknown layer slot ${asset.slot} for ${asset.id}`);
      if (!catalog.sourcePacks.includes(asset.sourcePack)) errors.push(`Unknown source pack ${asset.sourcePack} for ${asset.id}`);
    });
    const checkReference = (id, label) => {
      if (typeof id !== 'string' || !manifest.assets[id]) errors.push(`Missing or unknown ${label}: ${String(id)}`);
    };
    catalog.SHELLS.forEach((shell) => shell.views.forEach((view) => {
      checkReference(manifest.compositions.shells[shell.key]?.[view], `${view} shell composition ${shell.key}`);
      checkReference(manifest.compositions.shadows[shell.family]?.[view], `${view} shadow composition ${shell.family}`);
    }));
    catalog.SHELLS.filter((shell) => shell.layouts.includes('d-pad')).forEach((shell) => {
      checkReference(manifest.compositions.shellDpadOccluders?.[shell.key], `front D-pad shell cleanup ${shell.key}`);
    });
    catalog.BUTTON_SETS.forEach((buttons) => buttons.families.forEach((family) => {
      const controls = manifest.compositions.controls[buttons.key]?.[family];
      checkReference(controls?.front?.common, `front common controls ${buttons.key}/${family}`);
      checkReference(controls?.front?.abxy, `front ABXY controls ${buttons.key}/${family}`);
      if (buttons.layouts.includes('four-arrow')) checkReference(controls?.front?.fourArrow, `front four-arrow controls ${buttons.key}/${family}`);
      if (buttons.layouts.includes('d-pad')) checkReference(controls?.front?.dpad, `front D-pad controls ${buttons.key}/${family}`);
      checkReference(controls?.back, `rear controls ${buttons.key}/${family}`);
    }));
    catalog.DESIGNS.filter((design) => design.key !== 'none').forEach((design) => {
      const composition = manifest.compositions.designs[design.key];
      if (design.layouts.includes('four-arrow')) {
        checkReference(composition?.frontFourArrow, `front four-arrow design ${design.key}`);
        checkReference(composition?.backFourArrow, `rear four-arrow design ${design.key}`);
      }
      if (design.layouts.includes('d-pad')) {
        checkReference(composition?.frontDpad, `front D-pad design ${design.key}`);
        checkReference(composition?.backDpad, `rear D-pad design ${design.key}`);
      }
    });
    catalog.FAMILIES.forEach((family) => {
      checkReference(manifest.compositions.accents.colorful?.[family.key]?.abxy, `colorful ABXY ${family.key}`);
      checkReference(manifest.compositions.accents.colorful?.[family.key]?.fourArrow, `colorful four-arrow ${family.key}`);
      checkReference(manifest.compositions.faceStyles['official-lettered']?.[family.key], `official-lettered ABXY ${family.key}`);
    });
    catalog.ACCENTS.filter(accent => accent.key !== 'matching').forEach(accent => {
      const source = manifest.compositions.supplierFaceKits?.[accent.key];
      if (!accent.supplierSku || !source || source.supplierSku !== accent.supplierSku) {
        errors.push(`Missing verified supplier kit ${accent.key}.`);
        return;
      }
      if (!source.sourceSize?.every(value => Number.isFinite(value) && value > 0) || !/^[0-9a-f]{64}$/.test(source.sourceSha256)) errors.push(`Invalid supplier source metadata ${accent.key}.`);
      ['controls-abxy','controls-direction', ...(accent.kind === 'jelly' ? ['controls-common'] : [])].forEach(slot => checkReference(source.assetIds?.[slot], `${accent.name} ${slot}`));
      accent.includedParts.forEach(part => {
        const component = source.components?.[part];
        if (!component?.path || component.clip?.length !== 4 || !component.clip.every(Number.isFinite)) errors.push(`Missing ${accent.key} ${part} source geometry.`);
      });
    });
    catalog.CAPS.forEach((cap) => catalog.FAMILIES.forEach((family) => {
      checkReference(manifest.compositions.caps[cap.key]?.[family.key], `cap layer ${cap.key}/${family.key}`);
    }));
    return Object.freeze({ ok: errors.length === 0, errors: Object.freeze(errors) });
  }

  return Object.freeze({ version: CORE_VERSION, FACE_BUTTON_SCALE, URL_KEYS, availableShells, availableRightShells, availableButtonSets, availableRightButtonSets, availableDesigns, normalizeState, switchFamily, switchLayout, resolveGeometry, resolveArtworkMask, resolveButtonPlan, resolveLayers, parsePreviewState, stateToSearchParams, describeState, accessibilityLabel, reviewWarnings, createSequenceGate, validateDataContract });
});
